import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import BreadCrumb from '../../Common/BreadCrumb';
import {Button, Card, Col, Container, Dropdown, Form, Row, Spinner, Table, Alert as BootstrapAlert} from 'react-bootstrap';
import {useTranslation} from "react-i18next";
import ComparisonModal from "./Components/ComparisonModal";
import {generateFakeTransactionsWithAlerts} from "../../data/transactions";
import {
    useTable,
    useSortBy,
    useGlobalFilter,
    useFilters,
} from "react-table";
import ReceiptModal from "./Components/ReceiptModal";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {COLORS} from "../../Common/constants/colors";
import Flatpickr from "react-flatpickr";
import {getTenderTypeLabel, getTransactionTypeLabel} from "../../utils/labelUtils";
import {dashboardAPI} from "../../services/api";
import {mockStores} from "../../data/stores";
import {exportTransactionsToExcel, exportTransactionsToCSV, printTransactions} from "../../utils/exportUtils";

const TransactionList = () => {
    const {t} = useTranslation();
    document.title = t('TransactionList.title');

    // Check if user is demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    const [transactions, setTransactions] = useState<any[]>([]);
    const [stores, setStores] = useState<Store[]>([]);
    const [visibleCount, setVisibleCount] = useState(20);
    const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const loader = useRef(null);

    const [selectedReceipts, setSelectedReceipts] = useState<any[]>([]);
    const [showComparison, setShowComparison] = useState(false);

    const isMobile = window.innerWidth < 768;
    const MAX_SELECTION = isMobile ? 2 : 5;

    const toggleReceipt = (receipt: any) => {
        const exists = selectedReceipts.some(r => r.receiptNumber === receipt.receiptNumber);
        if (exists) {
            setSelectedReceipts(selectedReceipts.filter(r => r.receiptNumber !== receipt.receiptNumber));
        } else if (selectedReceipts.length < MAX_SELECTION) {
            setSelectedReceipts([...selectedReceipts, receipt]);
        }
    };

    // Fetch stores for store names
    useEffect(() => {
        if (isDemoUser) {
            setStores(mockStores);
            return;
        }
        const fetchStores = async () => {
            try {
                const response: any = await dashboardAPI.getStores();
                const storesData = Array.isArray(response) ? response : (response?.data || []);
                setStores(storesData.map((s: any) => ({
                    id: s.id || '',
                    name: s.name || '',
                    city: s.city || '',
                    province: s.province || '',
                    phone: s.phone || '',
                    address: s.address || '',
                    active: s.active !== false,
                    createdOn: s.createdOn || ''
                })));
            } catch (error) {
                console.error('Failed to fetch stores:', error);
            }
        };
        fetchStores();
    }, [isDemoUser]);

    // Fetch transactions from backend for real users
    useEffect(() => {
        if (isDemoUser) {
            // Use mock data for demo users
            const {transactions} = generateFakeTransactionsWithAlerts();
            setTransactions(transactions);
            return;
        }

        const fetchTransactions = async () => {
            setIsLoading(true);
            setError(null);
            try {
                console.log('📋 Fetching transactions from backend...', { page: currentPage, size: 20 });
                const response: any = await dashboardAPI.getTransactions({ 
                    page: currentPage, 
                    size: 20 
                });
                
                const transactionsData = response?.content || [];
                console.log('✅ Transactions received:', transactionsData.length, 'transactions');
                
                // Enrich transactions with store names
                const enrichedTransactions = transactionsData.map((tx: any) => {
                    const store = stores.find(s => s.id === tx.storeData?.id);
                    return {
                        ...tx,
                        storeData: store ? {
                            ...tx.storeData,
                            name: store.name,
                            city: store.city,
                            province: store.province,
                            phone: store.phone,
                            address: store.address
                        } : tx.storeData,
                        store: store?.name || ''
                    };
                });
                
                // Append transactions for infinite scroll (pagination)
                if (currentPage === 0) {
                    setTransactions(enrichedTransactions);
                } else {
                    setTransactions(prev => [...prev, ...enrichedTransactions]);
                }
                setTotalPages(response?.totalPages || 0);
                setTotalElements(response?.totalElements || 0);
            } catch (error: any) {
                console.error('❌ Failed to fetch transactions:', error);
                const status = error?.response?.status;
                const errorMessage = error?.response?.data?.message || error?.response?.data?.userMessage || error?.message;
                
                if (status === 403) {
                    setError(t('TransactionList.access_denied') || 'You do not have permission to access transactions. Please contact your administrator to assign the required role (ROLE_COMPANY_ADMIN, ROLE_STORE_MANAGER, or ROLE_GENERAL_ADMIN).');
                } else {
                    setError(errorMessage || 'Failed to fetch transactions');
                }
                setTransactions([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTransactions();
    }, [isDemoUser, currentPage, stores]);

    const handleObserver = useCallback((entries: any) => {
        const target = entries[0];
        if (target.isIntersecting && !isDemoUser && currentPage < totalPages - 1 && !isLoading) {
            // Load next page for real users
            setCurrentPage(prev => prev + 1);
        } else if (target.isIntersecting && isDemoUser) {
            // For demo users, use infinite scroll
            setVisibleCount((prev) => prev + 20);
        }
    }, [isDemoUser, currentPage, totalPages, isLoading]);

    useEffect(() => {
        if (isDemoUser) {
            // Only use infinite scroll for demo users
            const option = {
                root: null,
                rootMargin: "20px",
                threshold: 1.0,
            };
            const observer = new IntersectionObserver(handleObserver, option);
            if (loader.current) observer.observe(loader.current);
            return () => {
                if (loader.current) observer.unobserve(loader.current);
            };
        }
    }, [handleObserver, isDemoUser]);

    const DefaultColumnFilter = ({column: {filterValue, setFilter}}: any) => (
        <Form.Control
            size="sm"
            type="text"
            placeholder={t('TransactionList.filter')}
            value={filterValue ?? ''}
            onChange={(e) => setFilter(e.target.value)}
            style={{fontSize: '0.75rem', minWidth: isMobile ? '80px' : '100px'}}
        />
    );

    const SelectColumnFilter = ({
                                    column: { filterValue, setFilter, preFilteredRows, id },
                                    labelMapper
                                }: {
        column: any;
        labelMapper?: (value: string) => string;
    }) => {
        const options = useMemo(() => {
            const opts = new Set();
            preFilteredRows.forEach((row: any) => opts.add(row.values[id]));
            return [...opts.values() as any] as string[];
        }, [id, preFilteredRows]);

        return (
            <Form.Select
                size="sm"
                value={filterValue || ''}
                onChange={(e) => setFilter(e.target.value || undefined)}
                style={{fontSize: '0.75rem', minWidth: isMobile ? '80px' : '100px'}}
            >
                <option value="">{t('TransactionList.all')}</option>
                {options.map((option: string, i: number) => (
                    <option key={i} value={option}>
                        {labelMapper ? labelMapper(option) : option}
                    </option>
                ))}
            </Form.Select>
        );
    };

    const DateRangeColumnFilter = ({ column: { filterValue = [], setFilter } }: any) => {
        const [pickerKey, setPickerKey] = useState(0);

        const clearFilter = () => {
            setFilter(undefined);
            setPickerKey(prev => prev + 1);
        };

        return (
            <div className="d-flex gap-1 align-items-center flex-wrap">
                <Flatpickr
                    key={pickerKey} // 🔑 force remount on clear
                    options={{ mode: 'range', dateFormat: 'd.m.Y' }}
                    className="form-control form-control-sm"
                    placeholder={t('TransactionList.date_range')}
                    value={filterValue}
                    onChange={(range: any[]) => setFilter(range)}
                    style={{fontSize: '0.75rem', minWidth: isMobile ? '120px' : '150px', flex: '1 1 auto'}}
                />
                {filterValue?.length > 0 && (
                    <Button variant="outline-secondary" size="sm" onClick={clearFilter} style={{fontSize: '0.75rem'}}>
                        ✕
                    </Button>
                )}
            </div>
        );
    };


    const columns = useMemo(() => [
        {
            Header: "",
            id: "compareCheckbox",
            disableSortBy: true,
            Filter: () => null,
            Cell: ({ row }: any) => {
                const checked = selectedReceipts.some(r => r.receiptNumber === row.original.receiptNumber);
                return (
                    <Form.Check
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleReceipt(row.original)}
                        disabled={!checked && selectedReceipts.length >= MAX_SELECTION}
                    />
                );
            },
        },
        {
            Header: t('TransactionList.store'),
            accessor: (row: any) => row.storeData?.name || "",
            Filter: SelectColumnFilter,
            filter: "includes",
        },
        {
            Header: t('TransactionList.checkout'),
            accessor: "checkout",
            Filter: DefaultColumnFilter,
        },
        {
            Header: t('TransactionList.receipt_no'),
            accessor: "receiptNumber",
            Filter: DefaultColumnFilter,
            Cell: ({value, row}: any) => (
                <Button
                    variant="link"
                    style={{
                        padding: 0, 
                        fontSize: isMobile ? "0.75rem" : "0.875rem", 
                        color: COLORS.LINK,
                        textDecoration: 'underline',
                        wordBreak: 'break-word'
                    }}
                    onClick={() => setSelectedTransaction(row.original)}
                >
                    {value}
                </Button>
            ),
        },
        {
            Header: t('TransactionList.date'),
            accessor: "date",
            Filter: DateRangeColumnFilter,
            filter: 'dateBetween'
        },
        {
            Header: t('TransactionList.cashier_id'),
            accessor: "cashier",
            Filter: DefaultColumnFilter,
        },
        {
            Header: t('TransactionList.alerts'),
            accessor: "alert_count",
            Filter: DefaultColumnFilter,
        },
        {
            Header: t('TransactionList.total'),
            accessor: "total",
            Filter: DefaultColumnFilter,
        },
        {
            Header: t('TransactionList.transaction_type'),
            accessor: "transaction_type",
            Filter: (props: any) => (
                <SelectColumnFilter {...props} labelMapper={(val) => getTransactionTypeLabel(t, val)} />
            ),
            filter: "includes",
            Cell: ({ value }: any) => getTransactionTypeLabel(t, value),
        },
        {
            Header: t('TransactionList.tender_type'),
            accessor: "tender_type",
            Filter: (props: any) => (
                <SelectColumnFilter {...props} labelMapper={(val) => getTenderTypeLabel(t, val)} />
            ),
            filter: "includes",
            Cell: ({ value }: any) => getTenderTypeLabel(t, value),
        },
        {
            Header: t('TransactionList.credit_card'),
            accessor: "credit_card",
            Filter: DefaultColumnFilter,
        },
    ], [t, selectedReceipts]);

    const data = useMemo(() => transactions, [transactions]);

    const fuzzyTextFilterFn = (rows: any, id: number, filterValue: any) => {
        return rows.filter((row: any) => {
            const rowValue = row.values[id]?.toLowerCase?.() || '';
            return rowValue.includes(filterValue.toLowerCase());
        });
    };

    const parseDate = (value: string) => {
        const [day, month, yearAndTime] = value.split('.');
        const [year, time] = yearAndTime.split(' ');
        return new Date(`${year}-${month}-${day} ${time || '00:00:00'}`);
    };


    const dateBetweenFilterFn = (rows: any[], id: string, filterValue: Date[]) => {
        if (!filterValue || filterValue.length !== 2) return rows;
        const [start, end] = filterValue;

        return rows.filter(row => {
            const rawDate = row.values[id];
            if (!rawDate) return false;
            const rowDate = parseDate(rawDate);
            return rowDate >= start && rowDate <= end;
        });
    };


    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable(
        {
            columns,
            data,
            filterTypes: {
                fuzzyText: fuzzyTextFilterFn,
                text: (rows: any, id: number, filterValue: any) =>
                    rows.filter((row: any) => {
                        const rowValue = row.values[id];
                        return rowValue !== undefined
                            ? String(rowValue).toLowerCase().startsWith(filterValue.toLowerCase())
                            : true;
                    }),
                dateBetween: dateBetweenFilterFn
            },
            initialState: {sortBy: [{id: 'date', desc: true}]},
        },
        useGlobalFilter,
        useFilters,
        useSortBy
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            {label: t('Dashboard.header'), path: "/"},
                            {label: t('TransactionList.page_title'), path: "/transactions"},
                        ]}
                    />
                    {error && (
                        <Row>
                            <Col>
                                <BootstrapAlert variant="danger" onClose={() => setError(null)} dismissible>
                                    {error}
                                </BootstrapAlert>
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col xl={12}>
                            <Card>
                                <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <Card.Title className="mb-0">{t('TransactionList.page_title') || 'Transactions'}</Card.Title>
                                    {transactions.length > 0 && (
                                        <Dropdown>
                                            <Dropdown.Toggle variant="primary" size="sm" className="arrow-none">
                                                <i className="las la-download fs-18 align-middle me-1"></i>
                                                {t('TransactionList.export') || 'Export'}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu className="dropdown-menu-end">
                                                <Dropdown.Item onClick={() => exportTransactionsToExcel(rows.map((r: any) => r.original))}>
                                                    <i className="las la-file-excel fs-18 align-middle me-2 text-success"></i>
                                                    {t('TransactionList.export_excel') || 'Export to Excel'}
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => exportTransactionsToCSV(rows.map((r: any) => r.original))}>
                                                    <i className="las la-file-csv fs-18 align-middle me-2 text-info"></i>
                                                    {t('TransactionList.export_csv') || 'Export to CSV'}
                                                </Dropdown.Item>
                                                <Dropdown.Divider />
                                                <Dropdown.Item onClick={() => printTransactions(rows.map((r: any) => r.original))}>
                                                    <i className="las la-print fs-18 align-middle me-2 text-muted"></i>
                                                    {t('TransactionList.print') || 'Print'}
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    )}
                                </Card.Header>
                                <Card.Body>
                                    {selectedReceipts.length >= 2 && (
                                        <div className={`mt-3 ${isMobile ? 'text-center' : 'text-end'}`}>
                                            <Button 
                                                variant="primary" 
                                                onClick={() => setShowComparison(true)}
                                                size={isMobile ? 'sm' : undefined}
                                                className={isMobile ? 'w-100' : ''}
                                            >
                                                {t("TransactionList.compare_button")} ({selectedReceipts.length})
                                            </Button>
                                        </div>
                                    )}
                                    {isLoading && transactions.length === 0 ? (
                                        <div className="text-center py-4">
                                            <Spinner animation="border" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </Spinner>
                                        </div>
                                    ) : (
                                        <div 
                                            className="table-responsive" 
                                            style={{
                                                overflowX: 'auto', 
                                                WebkitOverflowScrolling: 'touch',
                                                maxWidth: '100%',
                                                margin: isMobile ? '0 -15px' : '0'
                                            }}
                                        >
                                            <Table 
                                                bordered 
                                                hover 
                                                {...getTableProps()} 
                                                className="table-nowrap align-middle mb-0"
                                                style={{
                                                    minWidth: isMobile ? '800px' : '100%',
                                                    fontSize: isMobile ? '0.8rem' : '0.875rem'
                                                }}
                                            >
                                            <thead className="table-light">
                                            {headerGroups.map((headerGroup: any) => (
                                                <tr {...headerGroup.getHeaderGroupProps()}>
                                                    {headerGroup.headers.map((column: any, index: number) => {
                                                        // Hide less important columns on mobile
                                                        const isMobileHidden = ['compareCheckbox', 'checkout', 'cashier', 'credit_card'].includes(column.id);
                                                        return (
                                                            <th 
                                                                {...column.getHeaderProps()}
                                                                className={isMobileHidden ? 'd-none d-md-table-cell' : ''}
                                                                style={{
                                                                    minWidth: isMobileHidden ? undefined : '120px',
                                                                    fontSize: '0.875rem',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                <div style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    flexWrap: 'wrap',
                                                                    gap: '4px'
                                                                }}>
                                                                    <span {...column.getSortByToggleProps()}
                                                                          style={{cursor: 'pointer', fontSize: '0.875rem'}}>
                                                                      {column.render("Header")}
                                                                        {column.isSorted &&
                                                                            (column.isSortedDesc ?
                                                                                <ArrowDownwardIcon fontSize="small" style={{fontSize: '16px'}}/> :
                                                                                <ArrowUpwardIcon fontSize="small" style={{fontSize: '16px'}}/>)}
                                                                    </span>
                                                                </div>
                                                                {column.canFilter && (
                                                                    <div style={{marginTop: 4, fontSize: '0.75rem'}}>
                                                                        {column.render("Filter")}
                                                                    </div>
                                                                )}
                                                            </th>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                            </thead>
                                            <tbody {...getTableBodyProps()}>
                                            {rows.length === 0 ? (
                                                <tr>
                                                    <td colSpan={columns.length} className="text-center py-4">
                                                        {t('Common.no_results_found')}
                                                    </td>
                                                </tr>
                                            ) : (
                                                rows.slice(0, visibleCount).map((row: any) => {
                                                    prepareRow(row);
                                                    return (
                                                        <tr {...row.getRowProps()}>
                                                            {row.cells.map((cell: any) => {
                                                                const isMobileHidden = ['compareCheckbox', 'checkout', 'cashier', 'credit_card'].includes(cell.column.id);
                                                                return (
                                                                    <td 
                                                                        {...cell.getCellProps()}
                                                                        className={isMobileHidden ? 'd-none d-md-table-cell' : ''}
                                                                        style={{
                                                                            fontSize: '0.875rem',
                                                                            padding: isMobile ? '0.5rem' : '0.75rem',
                                                                            verticalAlign: 'middle'
                                                                        }}
                                                                    >
                                                                        {cell.render("Cell")}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    );
                                                })
                                            )}
                                            </tbody>
                                        </Table>
                                    </div>
                                    )}
                                    {!isDemoUser && currentPage < totalPages - 1 && (
                                        <div
                                            ref={loader}
                                            style={{
                                                height: "50px",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center"
                                            }}
                                        >
                                            {isLoading && <Spinner animation="border" size="sm" />}
                                        </div>
                                    )}
                                    {isDemoUser && (
                                        <div
                                            ref={loader}
                                            style={{
                                                height: "50px",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center"
                                            }}
                                        >
                                        </div>
                                    )}
                                    <ReceiptModal
                                        selectedTransaction={selectedTransaction}
                                        setSelectedTransaction={setSelectedTransaction}
                                    />
                                    <ComparisonModal
                                        show={showComparison}
                                        onClose={() => setShowComparison(false)}
                                        receipts={selectedReceipts}
                                    />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default TransactionList;
